import { PORT_LETTERS } from "./drivetrains";

// Optional claws, lifts and arms: 1 motor or a 2-motor group, run by two controller buttons.
export const ATTACH_KINDS = {
  claw: { name: "Claw", pos: "Open", neg: "Close" },
  lift: { name: "Lift", pos: "Up", neg: "Down" },
  arm: { name: "Arm", pos: "Raise", neg: "Lower" },
  other: { name: "Attachment", pos: "Forwards", neg: "Backwards" },
};
export const KIND_ORDER = ["claw", "lift", "arm", "other"];

export const BUTTON_GROUPS = [
  { name: "Triggers", buttons: ["LT", "RT"] },
  { name: "Bumpers", buttons: ["LB", "RB"] },
  { name: "Face buttons", buttons: ["A", "B", "X", "Y"] },
  { name: "D-pad", buttons: ["UP", "DOWN", "LEFT", "RIGHT"] },
];
export const ALL_BUTTONS = BUTTON_GROUPS.flatMap((g) => g.buttons);
export const BUTTON_SHORT = { LT: "LT", RT: "RT", LB: "LB", RB: "RB", A: "A", B: "B", X: "X", Y: "Y", UP: "↑", DOWN: "↓", LEFT: "←", RIGHT: "→" };
export const BUTTON_NAME = {
  LT: "left trigger (LT)", RT: "right trigger (RT)", LB: "left bumper (LB)", RB: "right bumper (RB)",
  A: "A", B: "B", X: "X", Y: "Y", UP: "D-pad up", DOWN: "D-pad down", LEFT: "D-pad left", RIGHT: "D-pad right",
};
export const isTrigger = (b) => b === "LT" || b === "RT";
export const MAX_PORTS = PORT_LETTERS.length;

const PAIRS = [["RT", "LT"], ["RB", "LB"], ["Y", "A"], ["UP", "DOWN"], ["X", "B"], ["RIGHT", "LEFT"]];
const flip = (d) => (d === "F" ? "R" : "F");

// Direction of each motor in the group when the first ("open", "up"...) button is pressed.
export const motorDirs = (a) => (a.ports.length === 2 ? [a.dir, a.group === "same" ? a.dir : flip(a.dir)] : [a.dir]);

const drivePorts = (cfg) => Object.values(cfg.motors).map((m) => m.port);
export const usedPorts = (cfg) => [...drivePorts(cfg), ...(cfg.attachments || []).flatMap((a) => a.ports)];
export const freePortCount = (cfg) => MAX_PORTS - usedPorts(cfg).length;

export function newAttachment(cfg, size) {
  const atts = cfg.attachments || [];
  const taken = new Set(usedPorts(cfg));
  const usedButtons = new Set(atts.flatMap((a) => [a.pos, a.neg]));
  const [pos, neg] = PAIRS.find(([p, n]) => !usedButtons.has(p) && !usedButtons.has(n)) || PAIRS[0];
  const kinds = new Set(atts.map((a) => a.kind));
  return {
    kind: KIND_ORDER.find((k) => !kinds.has(k)) || "other",
    ports: PORT_LETTERS.filter((p) => !taken.has(p)).slice(0, size),
    dir: "F",
    group: "opposite",
    pos,
    neg,
    speed: 70,
    stop: "hold",
  };
}

// Keeps attachments that still fit after the drivetrain changes, moving them off ports the drivetrain now uses.
export function fitAttachments(cfg, attachments = []) {
  const taken = new Set(drivePorts(cfg));
  const kept = [];
  attachments.forEach((a) => {
    if (taken.size + a.ports.length > MAX_PORTS) return;
    const ports = a.ports.map((p) => {
      const port = taken.has(p) ? PORT_LETTERS.find((q) => !taken.has(q)) : p;
      taken.add(port);
      return port;
    });
    kept.push({ ...a, ports });
  });
  return kept;
}

export const buttonConflicts = (cfg) => {
  const seen = new Set();
  const dupes = new Set();
  (cfg.attachments || []).forEach((a) => [a.pos, a.neg].forEach((b) => (seen.has(b) ? dupes.add(b) : seen.add(b))));
  return dupes;
};

export const attachmentSummary = (a) => {
  const k = ATTACH_KINDS[a.kind] || ATTACH_KINDS.other;
  const group = a.ports.length === 2 ? ` (${a.group === "same" ? "same way" : "opposite ways"})` : "";
  return `${k.name}: port${a.ports.length === 2 ? "s" : ""} ${a.ports.join(" + ")}${group} · ${k.pos} ${BUTTON_SHORT[a.pos]} / ${k.neg} ${BUTTON_SHORT[a.neg]} · ${a.speed}% · ${a.stop === "hold" ? "holds" : "coasts"} when let go`;
};
